/**
 * Dialog zur Erstellung eines Qualifikations-/Weiterbildungs-Antrags.
 * Wird im Profil-Tab "Weiterbildung & Qualifikation" verwendet.
 *
 * Modes:
 *   - create-training, create-qualification : Neuer Eintrag
 *   - update-training, update-qualification : Änderungsantrag (initial mit aktuellen Werten)
 */
import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Loader2, Upload, FileText, X } from 'lucide-react';
import { DatePickerDE } from '../../components/ui/date-picker-de';
import { toast } from 'sonner';
import { qualificationProposalsAPI } from '../../lib/api';

const TRAINING_FIELDS = [
  { key: 'name', label: 'Titel der Weiterbildung *', required: true },
  { key: 'provider', label: 'Anbieter / Veranstalter' },
  { key: 'completed_date', label: 'Abgeschlossen am', type: 'date' },
  { key: 'expiry_date', label: 'Gültig bis (optional)', type: 'date' },
  { key: 'hours', label: 'Umfang (Stunden)', type: 'number' },
  { key: 'description', label: 'Beschreibung (optional)', type: 'textarea' },
];

const QUALIFICATION_FIELDS = [
  { key: 'name', label: 'Bezeichnung *', required: true },
  { key: 'institution', label: 'Institution / Aussteller' },
  { key: 'date', label: 'Erworben am', type: 'date' },
  { key: 'expiry_date', label: 'Gültig bis (optional)', type: 'date' },
];

export const ProposalDialog = ({
  open, onOpenChange,
  kind,           // "training" | "qualification"
  action,         // "create" | "update"
  initial = {},   // Werte zum Vorbefüllen bei "update"
  onSuccess,
}) => {
  const [form, setForm] = useState({});
  const [file, setFile] = useState(null);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const ALLOWED_EXTS = ['pdf', 'png', 'jpg', 'jpeg', 'webp'];
  const MAX_SIZE = 10 * 1024 * 1024;

  const acceptFile = (f) => {
    if (!f) return;
    const ext = f.name.split('.').pop()?.toLowerCase();
    if (!ALLOWED_EXTS.includes(ext)) {
      toast.error(`Dateityp .${ext} nicht erlaubt (PDF, PNG, JPG)`);
      return;
    }
    if (f.size > MAX_SIZE) {
      toast.error('Datei zu groß (max. 10 MB)');
      return;
    }
    setFile(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) acceptFile(dropped);
  };

  const fields = kind === 'training' ? TRAINING_FIELDS : QUALIFICATION_FIELDS;
  const label = kind === 'training' ? 'Weiterbildung' : 'Qualifikation';

  useEffect(() => {
    if (open) {
      // Bei Update: aktuelle Werte vorbefüllen
      const seed = {};
      fields.forEach(f => {
        if (initial[f.key] != null) seed[f.key] = initial[f.key];
      });
      // Bei Trainings ist im Schema "title" das Hauptfeld; UI nutzt "name"
      if (kind === 'training' && initial.title && !seed.name) seed.name = initial.title;
      // „Abgeschlossen am" (completed_date) ggf. aus dem kanonischen Feld `date`
      // vorbefüllen, falls der Eintrag (z. B. per Admin/Schnell-Erfassung) nur
      // `date` besitzt – sonst wirkt das Feld leer, obwohl ein Datum existiert.
      if (kind === 'training' && seed.completed_date == null && initial.date != null) {
        seed.completed_date = initial.date;
      }
      setForm(seed);
      setFile(null);
      setReason('');
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  const handleSubmit = async () => {
    if (!form.name?.trim()) {
      toast.error('Bitte einen Titel/Bezeichnung angeben');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.hours !== undefined && payload.hours !== '') {
        payload.hours = Number(payload.hours);
      }
      await qualificationProposalsAPI.create({
        kind,
        action,
        target_id: action === 'update' ? initial.id : undefined,
        payload,
        reason: reason.trim() || undefined,
        file: file || undefined,
      });
      toast.success(action === 'create'
        ? `${label}-Antrag eingereicht — wartet auf Freigabe`
        : `Änderungsantrag eingereicht — wartet auf Freigabe`);
      onOpenChange(false);
      onSuccess?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Antrag konnte nicht eingereicht werden');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" onCloseAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>
            {action === 'create' ? `Neue ${label} beantragen` : `${label} ändern`}
          </DialogTitle>
          <DialogDescription>
            Dein Antrag wird an die zuständige Leitung gesendet und erscheint erst nach Freigabe in deinem Profil.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {fields.map((f) => (
            <div key={f.key} className="space-y-1">
              <Label htmlFor={`pf-${f.key}`}>{f.label}</Label>
              {f.type === 'date' ? (
                <DatePickerDE
                  value={form[f.key] || ''}
                  onChange={(v) => update(f.key, v)}
                  placeholder="TT.MM.JJJJ"
                />
              ) : f.type === 'textarea' ? (
                <Textarea
                  id={`pf-${f.key}`}
                  value={form[f.key] || ''}
                  onChange={(e) => update(f.key, e.target.value)}
                  rows={2}
                  data-testid={`proposal-field-${f.key}`}
                />
              ) : (
                <Input
                  id={`pf-${f.key}`}
                  type={f.type || 'text'}
                  value={form[f.key] ?? ''}
                  onChange={(e) => update(f.key, e.target.value)}
                  data-testid={`proposal-field-${f.key}`}
                />
              )}
            </div>
          ))}

          {/* Datei-Upload mit Drag & Drop */}
          <div className="space-y-1">
            <Label>Nachweis hochladen (optional, PDF/JPG/PNG, max. 10 MB)</Label>
            {file ? (
              <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm">
                <FileText className="h-4 w-4 text-primary" />
                <span className="flex-1 truncate">{file.name}</span>
                <span className="text-xs text-muted-foreground">
                  {(file.size / 1024).toFixed(0)} KB
                </span>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setFile(null)} data-testid="proposal-file-remove">
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div
                onClick={() => fileInputRef.current?.click()}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
                onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); }}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); }}
                onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); }}
                onDrop={handleDrop}
                role="button"
                tabIndex={0}
                className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed px-3 py-6 text-sm transition-colors
                  ${isDragging
                    ? 'border-primary bg-primary/5 text-primary'
                    : 'border-border bg-muted/20 text-muted-foreground hover:border-primary/50 hover:bg-muted/40'}`}
                data-testid="proposal-file-dropzone"
              >
                <Upload className={`h-6 w-6 ${isDragging ? 'text-primary' : 'text-muted-foreground'}`} />
                <div className="text-center">
                  <p className="font-medium">
                    {isDragging ? 'Datei hier loslassen' : 'Datei hierher ziehen'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    oder <span className="underline">durchsuchen</span>
                  </p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.webp"
                  onChange={(e) => acceptFile(e.target.files?.[0])}
                  className="hidden"
                  data-testid="proposal-file-input"
                />
              </div>
            )}
          </div>

          {/* Begründung */}
          <div className="space-y-1">
            <Label htmlFor="proposal-reason">Anmerkung an Vorgesetzte:n (optional)</Label>
            <Textarea
              id="proposal-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="z.B. Zertifikat von externer Fortbildung 2026"
              rows={2}
              data-testid="proposal-reason"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Abbrechen
          </Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="proposal-submit-btn">
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Antrag einreichen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProposalDialog;
